// Patches global fetch to inject SECRET_HASH and client_secret into Cognito API calls.
// Amplify v6 browser SDK doesn't support client secrets natively.

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || '';
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET || '';
const REGION = process.env.REACT_APP_REGION || 'eu-north-1';
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';
const COGNITO_IDP_URL = `https://cognito-idp.${REGION}.amazonaws.com`;

console.log('[CognitoHelper] CLIENT_ID:', CLIENT_ID ? CLIENT_ID.slice(0, 8) + '...' : 'MISSING');
console.log('[CognitoHelper] CLIENT_SECRET:', CLIENT_SECRET ? CLIENT_SECRET.slice(0, 4) + '***' : 'MISSING');
console.log('[CognitoHelper] REGION:', REGION);
console.log('[CognitoHelper] COGNITO_DOMAIN:', COGNITO_DOMAIN || 'MISSING');

async function computeSecretHash(username) {
  if (!CLIENT_SECRET || !username) return null;
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(CLIENT_SECRET),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(username + CLIENT_ID)
  );
  return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

const HASH_TARGETS = {
  'AWSCognitoIdentityProviderService.InitiateAuth': (body) => body.AuthParameters?.USERNAME,
  'AWSCognitoIdentityProviderService.RespondToAuthChallenge': (body) => body.ChallengeResponses?.USERNAME,
  'AWSCognitoIdentityProviderService.SignUp': (body) => body.Username,
  'AWSCognitoIdentityProviderService.ConfirmSignUp': (body) => body.Username,
  'AWSCognitoIdentityProviderService.ForgotPassword': (body) => body.Username,
  'AWSCognitoIdentityProviderService.ConfirmForgotPassword': (body) => body.Username,
  'AWSCognitoIdentityProviderService.ResendConfirmationCode': (body) => body.Username,
};

// Safely extract URL and body text from any fetch input combination
async function readBodyAsText(body) {
  if (!body) return null;
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof FormData) return new URLSearchParams(body).toString();
  if (body instanceof ArrayBuffer) return new TextDecoder().decode(body);
  if (body instanceof Blob) return await body.text();
  // ReadableStream
  if (typeof body.getReader === 'function') {
    const reader = body.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const merged = new Uint8Array(chunks.reduce((a, c) => a + c.length, 0));
    let offset = 0;
    for (const chunk of chunks) { merged.set(chunk, offset); offset += chunk.length; }
    return new TextDecoder().decode(merged);
  }
  return String(body);
}

function headersToObject(h) {
  if (!h) return {};
  if (h instanceof Headers) return Object.fromEntries(h.entries());
  if (typeof h === 'object') return { ...h };
  return {};
}

async function decomposeFetchArgs(input, init) {
  let url = '';
  let bodyText = null;
  let headers = {};
  let fetchOptions = {};

  if (input instanceof Request) {
    url = input.url;
    const cloned = input.clone();
    try { bodyText = await cloned.text(); } catch (e) { bodyText = null; }
    headers = headersToObject(input.headers);
    fetchOptions = {
      method: input.method,
      headers,
      credentials: input.credentials,
      mode: input.mode,
      cache: input.cache,
      redirect: input.redirect,
    };
    if (init) {
      if (init.headers) headers = headersToObject(init.headers);
      if (init.body !== undefined) bodyText = await readBodyAsText(init.body);
      fetchOptions = { ...fetchOptions, ...init, headers };
    }
  } else {
    url = typeof input === 'string' ? input : String(input);
    headers = headersToObject(init?.headers);
    bodyText = init?.body ? await readBodyAsText(init.body) : null;
    fetchOptions = { ...(init || {}), headers };
  }

  return { url, bodyText, headers, fetchOptions };
}

function getHeader(headers, name) {
  if (!headers) return null;
  if (headers[name]) return headers[name];
  const lower = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}

let installed = false;

export function installSecretHashInterceptor() {
  if (installed) return;
  if (!CLIENT_SECRET) {
    console.warn('[CognitoHelper] No CLIENT_SECRET — interceptor NOT installed');
    return;
  }

  installed = true;
  const originalFetch = window.fetch.bind(window);
  console.log('[CognitoHelper] Interceptor installing...');

  window.fetch = async function patchedFetch(input, init) {
    // Determine the URL without consuming the request body
    let url = '';
    if (input instanceof Request) {
      url = input.url;
    } else {
      url = typeof input === 'string' ? input : String(input);
    }

    // Only intercept Cognito IDP and OAuth token URLs — pass everything else
    // (including AppSync) through completely unmodified
    const isCognitoIdp = url.startsWith(COGNITO_IDP_URL);
    const isOAuthToken = url.includes('/oauth2/token');

    if (!isCognitoIdp && !isOAuthToken) {
      return originalFetch(input, init);
    }

    // Only decompose for Cognito URLs that we need to modify
    let decomposed;
    try {
      decomposed = await decomposeFetchArgs(input, init);
    } catch (e) {
      console.warn('[CognitoHelper] Failed to decompose fetch args:', e);
      return originalFetch(input, init);
    }

    const { bodyText, headers, fetchOptions } = decomposed;

    console.log('[CognitoHelper] fetch ->', url.slice(0, 80), bodyText ? `(body: ${bodyText.length} chars)` : '(no body)');

    // ===== 1. OAuth /oauth2/token endpoint — inject client_secret as form param =====
    if (isOAuthToken && bodyText) {
      try {
        const params = new URLSearchParams(bodyText);
        console.log('[CognitoHelper] /oauth2/token params before:', Object.fromEntries(params.entries()));

        if (!params.has('client_secret')) {
          params.set('client_secret', CLIENT_SECRET);
          const newBody = params.toString();
          console.log('[CognitoHelper] Injected client_secret into /oauth2/token');
          return originalFetch(url, { ...fetchOptions, body: newBody });
        }
      } catch (e) {
        console.error('[CognitoHelper] /oauth2/token intercept error:', e);
      }
      return originalFetch(url, { ...fetchOptions, body: bodyText });
    }

    // ===== 2. Cognito IDP JSON API — inject SECRET_HASH only for operations that need it =====
    if (isCognitoIdp && bodyText) {
      const target = getHeader(headers, 'X-Amz-Target');
      console.log(`[CognitoHelper] IDP call: ${target || 'unknown'}`);

      if (target && target in HASH_TARGETS) {
        try {
          const body = JSON.parse(bodyText);
          const getUsernameFn = HASH_TARGETS[target];
          const username = getUsernameFn ? getUsernameFn(body) : null;

          console.log(`[CognitoHelper] IDP ${target} needs SECRET_HASH, username: ${username}`);

          if (username) {
            const hash = await computeSecretHash(username);
            if (hash) {
              if (body.AuthParameters) body.AuthParameters.SECRET_HASH = hash;
              else if (body.ChallengeResponses) body.ChallengeResponses.SECRET_HASH = hash;
              else body.SecretHash = hash;

              console.log(`[CognitoHelper] Injected SECRET_HASH for ${username}`);
              return originalFetch(url, { ...fetchOptions, body: JSON.stringify(body) });
            }
          }
        } catch (e) {
          console.error('[CognitoHelper] IDP SECRET_HASH inject error:', e);
        }
      }
      // Pass through IDP calls that don't need SECRET_HASH
      return originalFetch(url, { ...fetchOptions, body: bodyText });
    }

    // Fallback (shouldn't reach here given the early return above)
    return originalFetch(input, init);
  };

  console.log('[CognitoHelper] Interceptor installed successfully');
}

export { computeSecretHash };
